import React, {
  useEffect, useRef, useState,
} from 'react';

import PropTypes from 'prop-types';
import {
  Container, useMediaQuery, Typography,
} from '@material-ui/core';

import {
  Loader, ProgressBar, WhatsappFloat,
} from '../../../../../components';
import useLoading from '../../../../../hooks/useLoading';
import { useNodesFields } from '../../../../../hooks/useNodesFields';
import { getIsARecoverClient } from '../../../../../utils/clientData';
import { getProcess } from '../../../../../utils/auth';
import useStyles from './useStyles';
import { NodeContainer } from '../../NodeContainer';
import { InformationDialog } from '../../../../../components/InformationDialog';
import { COMPANY, ERROR_NO_NODES_TO_LOAD, PAGE_NODOS_FORM_NAME } from '../../../../../constants';
import { useLogger } from '../../../../../hooks/useLogger';
import { LogLevel } from '../../../../../api/logs';
import { getPageConfig } from '../../../../../api/pageConfigs';
import ModalWelcomeMessage from '../ModalWelcomeMessage';
import { DataNodosFormChiplo } from '../../../../../data';
import { objectUtils } from '../../../../../utils';

const DEFAULT_PAGE_CONFIG = DataNodosFormChiplo;

export const ModalNodosChiplo = ({ onlyEmptyNodes, onFinish }) => {
  const {
    logError,
  } = useLogger();
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width:600px)');
  const { isLoading, startLoading, endLoading } = useLoading();
  const {
    isLoading: isLoadingGeneral,
    startLoading: startLoadingGeneral,
    endLoading: endLoadingGeneral,
  } = useLoading();
  const [openModalSendEmail, setOpenModalSendEmail] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [pageConfig, setPageConfig] = useState(DEFAULT_PAGE_CONFIG);

  const formRef = useRef(null);

  const {
    nodesToShow,
    activeNode,
    isLastNode,
    nodesLoaded,
    fetchNodesAndFieldsByProcess,
    goNextActiveNode,
    loadNodesFields,
    sendDocumentEmail,
    saveNodeFieldValues,
    getDescriptionEmail,
    isActiveNodeDocumentNode,
    sendFieldValuesToBatch,
  } = useNodesFields({ applyModalDissabled: true, startLoading, endLoading });

  const handleAcceptTerms = () => {
    setAcceptedTerms(true);
  };

  const handleSendEmail = async () => {
    startLoadingGeneral();
    await sendDocumentEmail();
    setOpenModalSendEmail(false);
    endLoadingGeneral();
  };

  const fetchNodesPageConfig = async () => {
    try {
      const pageConfg = await getPageConfig(COMPANY, PAGE_NODOS_FORM_NAME);
      if (objectUtils.isValidObject(pageConfg)) {
        setPageConfig(pageConfg);
      }
    } catch (error) {
      setPageConfig(DEFAULT_PAGE_CONFIG);
    }
  };

  const fetchData = async () => {
    try {
      startLoading();
      await fetchNodesPageConfig();
      const processId = getProcess();
      const isARecoveredClient = getIsARecoverClient() || onlyEmptyNodes || false;
      const nodesAndFields = await fetchNodesAndFieldsByProcess(processId, isARecoveredClient);
      loadNodesFields(nodesAndFields, true);
      endLoading();
    } catch (error) {
      if (error.message === ERROR_NO_NODES_TO_LOAD) {
        await onFinish();
      } else {
        await logError({
          error,
          level: LogLevel.CRITICAL,
          message: 'Error al cargar los nodos de perfilamiento',
          origin: 'CreditProfilingModalChiplo',
        });
      }
      endLoading();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    startLoading();

    if (isLastNode) {
      await saveNodeFieldValues(activeNode, true);
      await sendFieldValuesToBatch();
      await onFinish();
    } else {
      saveNodeFieldValues(activeNode, true);
      goNextActiveNode();
    }
    endLoading();
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    formRef?.current?.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [activeNode]);

  const showWelcomeMessage = () => {
    const show = nodesLoaded && pageConfig?.show && !acceptedTerms;
    return show;
  };

  const renderFormContent = () => {
    if (!nodesLoaded) return null;

    return (
      <>
        <div className={classes.titleContainer}>
          <Typography className={classes.title}>Completa lo siguiente</Typography>
          <Typography className={classes.subtitle}>{nodesToShow[activeNode]?.name}</Typography>
        </div>
        <div className={classes.progressContainer}>
          <ProgressBar
            currentProgress={nodesToShow.length > 0 ? activeNode + 1 : activeNode}
            totalProgress={nodesToShow.length}
          />
        </div>
        {isMobile && isActiveNodeDocumentNode() && (
          <Typography component="h2" className={classes.subtitle}>
            <button
              className={classes.buttonLink}
              onClick={() => setOpenModalSendEmail(true)}
              type="button"
            >
              ¿Quieres cargar los documentos desde tu computadora?
            </button>
          </Typography>
        )}
        <div ref={formRef} className={classes.scrollContainer}>
          <NodeContainer
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            startLoading={startLoading}
            endLoading={endLoading}
            showDocuments
          />
        </div>
      </>
    );
  };

  return (
    <Container
      disableGutters
      className={classes.mainContainer}
      maxWidth={false}
    >
      <Loader open={isLoading || isLoadingGeneral} />
      {showWelcomeMessage() ? (
        <ModalWelcomeMessage
          onAccept={handleAcceptTerms}
          title={pageConfig.title}
          bodyContent={pageConfig.bodyContent}
          privacyContent={pageConfig.privacyContent}
          footerContent={pageConfig.footerContent}
          extraContent={pageConfig.extraContent}
          continueButtonText={pageConfig.continueButtonText}
        />
      ) : (
        renderFormContent()
      )}

      <InformationDialog
        showModal={openModalSendEmail}
        onSubmit={handleSendEmail}
        buttonTitle="Enviar"
        onClose={() => setOpenModalSendEmail(false)}
        descripcion={getDescriptionEmail()}
      />
      <WhatsappFloat />
    </Container>
  );
};

ModalNodosChiplo.propTypes = {
  onlyEmptyNodes: PropTypes.bool,
  onFinish: PropTypes.func,
};

ModalNodosChiplo.defaultProps = {
  onlyEmptyNodes: false,
  onFinish: () => {},
};
